import { useState, useEffect } from "react";
import i18next from "i18next";
import { BookingLanguage } from "@/types/booking";
import { trackEvent } from "@/utils/analytics";
import { normalizeLanguage } from "@/components/product-step/types";
import { languageMap } from "@/i18n";
import { safeLocalStorage } from "@/utils/safeStorage";
import {
  DEFAULT_MARKET,
  coerceLanguageForMarket,
  parseMarketFromUrl,
} from "@/config/market";

const detectInitialLanguage = (): BookingLanguage => {
  // First check URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const localeParam = urlParams.get("locale");

  // Handle both 'de' and 'de-DE' formats for better usability
  if (localeParam === "de" || localeParam === "de-DE") {
    return "de-DE";
  }

  // Handle both 'fr' and 'fr-FR' formats
  if (localeParam === "fr" || localeParam === "fr-FR") {
    return "fr-FR";
  }

  // Handle both 'nl' and 'nl-NL' formats
  if (localeParam === "nl" || localeParam === "nl-NL") {
    return "nl-NL";
  }

  // Handle both 'en' and 'en-US' formats
  if (localeParam === "en" || localeParam === "en-US") {
    return "en-US";
  }

  // Then check localStorage (potentially from Framer site)
  const storedLang = safeLocalStorage.getItem("userLanguage");
  if (
    storedLang === "de-DE" ||
    storedLang === "en-US" ||
    storedLang === "fr-FR" ||
    storedLang === "nl-NL"
  ) {
    return storedLang as BookingLanguage;
  }

  // Then check browser's preferred language
  const browserLang = navigator.language;
  if (browserLang.startsWith("de")) {
    return "de-DE";
  }
  if (browserLang.startsWith("fr")) {
    return "fr-FR";
  }
  if (browserLang.startsWith("nl")) {
    return "nl-NL";
  }

  // Default to English
  return "en-US";
};

// Coerce the detected language into one the active market offers, so a
// `?country=de` deep link (or a persisted French choice) never starts the German
// market in French — French is Switzerland-only.
const getInitialLanguage = (): BookingLanguage => {
  const market =
    parseMarketFromUrl(new URLSearchParams(window.location.search)) ??
    DEFAULT_MARKET;
  return coerceLanguageForMarket(market, detectInitialLanguage());
};

export const useLanguage = () => {
  const [language, setLanguage] =
    useState<BookingLanguage>(getInitialLanguage());

  // Effect to sync language with localStorage
  useEffect(() => {
    safeLocalStorage.setItem("userLanguage", language);
  }, [language]);

  // Effect to sync language with i18next
  useEffect(() => {
    const i18nLang = languageMap[language] || "en";
    if (i18next.isInitialized && i18next.language !== i18nLang) {
      i18next.changeLanguage(i18nLang);
    }
  }, [language]);

  // Note: document <title> and <html lang> are handled centrally by
  // useDocumentMeta (mounted once at the app root). The previous title effect
  // here was gated on an undefined window.setPageTitle global and never ran.

  // Effect to listen for language changes from the main site
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "userLanguage") {
        const newLang = e.newValue as BookingLanguage;
        if (
          newLang === "de-DE" ||
          newLang === "en-US" ||
          newLang === "fr-FR" ||
          newLang === "nl-NL"
        ) {
          // Coerce by market like the init/URL paths — a tab on ?country=de
          // must not flip to French (which the DE market doesn't offer).
          const market =
            parseMarketFromUrl(new URLSearchParams(window.location.search)) ??
            DEFAULT_MARKET;
          setLanguage(coerceLanguageForMarket(market, newLang));
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Effect to listen for URL parameter changes
  useEffect(() => {
    const handleUrlChange = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const localeParam = urlParams.get("locale");

      let detected: BookingLanguage | null = null;
      if (localeParam === "de" || localeParam === "de-DE") {
        detected = "de-DE";
      } else if (localeParam === "fr" || localeParam === "fr-FR") {
        detected = "fr-FR";
      } else if (localeParam === "nl" || localeParam === "nl-NL") {
        detected = "nl-NL";
      } else if (localeParam === "en" || localeParam === "en-US") {
        detected = "en-US";
      }

      if (detected) {
        // French is Switzerland-only: coerce the URL locale against the active
        // market so ?country=de&locale=fr resolves to German, not French.
        const market = parseMarketFromUrl(urlParams) ?? DEFAULT_MARKET;
        const coerced = coerceLanguageForMarket(market, detected);
        setLanguage(coerced);
        console.log(
          `Set language to ${coerced} from URL param (market ${market})`,
        );
      }
    };

    // Listen for popstate event (back/forward navigation)
    window.addEventListener("popstate", handleUrlChange);

    // Initial check
    handleUrlChange();

    return () => window.removeEventListener("popstate", handleUrlChange);
  }, []);

  const setLanguageWithTracking = (newLang: BookingLanguage) => {
    setLanguage(newLang);
    trackEvent({
      event: "language_change",
      props: {
        from: language,
        to: newLang,
      },
    });
  };

  return {
    language,
    setLanguage: setLanguageWithTracking,
  };
};
